import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLanguage } from '../context/LanguageContext';

const API = '';

// ─── Web Audio API ───
let audioCtx = null;
function getAudioCtx() {
  if (!audioCtx) audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  return audioCtx;
}

function playSound(typ) {
  try {
    const ctx = getAudioCtx();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);

    if (typ === 'start') {
      osc.frequency.value = 880;
      gain.gain.value = 0.25;
      osc.start();
      setTimeout(() => osc.stop(), 150);
    } else if (typ === 'warn') {
      osc.frequency.value = 660;
      osc.type = 'triangle';
      gain.gain.value = 0.2;
      osc.start();
      setTimeout(() => osc.stop(), 120);
    } else if (typ === 'alarm') {
      osc.frequency.value = 330;
      osc.type = 'square';
      gain.gain.value = 0.15;
      osc.start();
      setTimeout(() => osc.stop(), 500);
    } else if (typ === 'success') {
      osc.frequency.value = 1047;
      gain.gain.value = 0.2;
      osc.start();
      setTimeout(() => { osc.frequency.value = 1319; }, 100);
      setTimeout(() => osc.stop(), 200);
    } else if (typ === 'error') {
      osc.frequency.value = 220;
      osc.type = 'square';
      gain.gain.value = 0.15;
      osc.start();
      setTimeout(() => osc.stop(), 400);
    }
  } catch { /* Audio not available */ }
}


export default function PackStationDisplay() {
  const { t } = useLanguage();

  // ─── Auth State ───
  const [authState, setAuthState] = useState('checking'); // checking | pin | authenticated
  const [stationJwt, setStationJwt] = useState(null);
  const [stationId, setStationId] = useState(null);
  const [stationInfo, setStationInfo] = useState(null);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState('');
  const stationJwtRef = useRef(null);

  // ─── Display State ───
  const [displayState, setDisplayState] = useState('idle'); // idle | running | completed
  const [stationName, setStationName] = useState('');
  const [maName, setMaName] = useState('');
  const [session, setSession] = useState(null);
  const [config, setConfig] = useState({ threshold_green: 50, threshold_yellow: 25, sound_enabled: true, auto_timeout_min: 15, mode: 'live' });
  const [previousResult, setPreviousResult] = useState(null);
  const [lastResult, setLastResult] = useState(null);
  const [remaining, setRemaining] = useState(0);
  const [elapsed, setElapsed] = useState(0);
  const [clock, setClock] = useState(new Date());
  const [flash, setFlash] = useState(null);
  const [identifiedMa, setIdentifiedMa] = useState(null);

  // Refs for stale-closure protection
  const displayStateRef = useRef(displayState);
  const sessionRef = useRef(session);
  const configRef = useRef(config);
  const warnPlayedRef = useRef(false);
  const alarmPlayedRef = useRef(false);
  const completedTimerRef = useRef(null);

  useEffect(() => { displayStateRef.current = displayState; }, [displayState]);
  useEffect(() => { sessionRef.current = session; }, [session]);
  useEffect(() => { configRef.current = config; }, [config]);

  // ─── Auth: Check saved JWT on mount ───
  useEffect(() => {
    const saved = localStorage.getItem('pack_station_jwt');
    if (saved) {
      // Verify JWT
      fetch(`${API}/api/pack-timer/verify`, {
        headers: { 'x-pack-station-token': saved },
      })
        .then(r => r.json())
        .then(data => {
          if (data.valid) {
            setStationJwt(saved);
            stationJwtRef.current = saved;
            setStationId(data.station_id);
            setStationInfo({ name: data.station_name, nr: data.station_nr });
            setStationName(data.station_name || '');
            setAuthState('authenticated');
          } else {
            localStorage.removeItem('pack_station_jwt');
            setAuthState('pin');
          }
        })
        .catch(() => {
          localStorage.removeItem('pack_station_jwt');
          setAuthState('pin');
        });
    } else {
      setAuthState('pin');
    }
  }, []);

  // ─── PIN Submit ───
  const submitPin = useCallback(async (pin) => {
    setPinError('');
    try {
      const res = await fetch(`${API}/api/pack-timer/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin }),
      });
      const data = await res.json();
      if (data.ok && data.token) {
        localStorage.setItem('pack_station_jwt', data.token);
        setStationJwt(data.token);
        stationJwtRef.current = data.token;
        setStationId(data.station_id);
        setStationInfo({ name: data.station_name, nr: data.station_nr });
        setStationName(data.station_name || '');
        setAuthState('authenticated');
        setPinInput('');
      } else {
        setPinError(data.error || 'Ungültige PIN');
        setPinInput('');
        if (configRef.current.sound_enabled) playSound('error');
      }
    } catch {
      setPinError('Verbindungsfehler');
      setPinInput('');
    }
  }, []);

  // ─── Logout ───
  const logout = useCallback(() => {
    localStorage.removeItem('pack_station_jwt');
    setStationJwt(null);
    stationJwtRef.current = null;
    setStationId(null);
    setStationInfo(null);
    setAuthState('pin');
    setDisplayState('idle');
    setSession(null);
    setLastResult(null);
    setIdentifiedMa(null);
    setMaName('');
    setPinInput('');
  }, []);

  // ─── PIN auto-submit when 4 digits ───
  useEffect(() => {
    if (pinInput.length === 4 && authState === 'pin') {
      submitPin(pinInput);
    }
  }, [pinInput, authState, submitPin]);

  // ─── API Helper ───
  const apiFetch = useCallback(async (url, opts = {}) => {
    const headers = { 'x-pack-station-token': stationJwtRef.current, ...opts.headers };
    if (opts.body) headers['Content-Type'] = 'application/json';
    const res = await fetch(`${API}${url}`, { ...opts, headers });
    if (res.status === 401) { logout(); return null; }
    return res.json();
  }, [logout]);

  // ─── Clock ───
  useEffect(() => {
    const iv = setInterval(() => setClock(new Date()), 1000);
    return () => clearInterval(iv);
  }, []);

  // ─── Initial status check ───
  useEffect(() => {
    if (authState !== 'authenticated' || !stationId) return;
    apiFetch(`/api/pack-timer/station/${stationId}/status`).then(data => {
      if (!data) return;
      if (data.config) setConfig(c => ({ ...c, ...data.config }));
      if (data.status === 'running' && data.session) {
        setSession(data.session);
        setStationName(data.station_name || stationInfo?.name || '');
        setDisplayState('running');
        warnPlayedRef.current = false;
        alarmPlayedRef.current = false;
      }
    });
  }, [authState, stationId, apiFetch, stationInfo]);

  // ─── Idle polling (every 5s) ───
  useEffect(() => {
    if (displayState !== 'idle' || authState !== 'authenticated' || !stationId) return;
    const iv = setInterval(async () => {
      const data = await apiFetch(`/api/pack-timer/station/${stationId}/status`);
      if (!data) return;
      if (data.config) setConfig(c => ({ ...c, ...data.config }));
      if (data.status === 'running' && data.session) {
        setSession(data.session);
        setDisplayState('running');
        warnPlayedRef.current = false;
        alarmPlayedRef.current = false;
      }
    }, 5000);
    return () => clearInterval(iv);
  }, [displayState, authState, stationId, apiFetch]);

  // ─── Timer countdown (100ms) ───
  useEffect(() => {
    if (displayState !== 'running' || !session) return;
    const iv = setInterval(() => {
      const s = sessionRef.current;
      if (!s) return;
      const elapsedMs = Date.now() - new Date(s.started_at).getTime();
      const sollMs = s.soll_minuten * 60 * 1000;
      const remainMs = sollMs - elapsedMs;

      setRemaining(remainMs);
      setElapsed(elapsedMs);

      // Sound: 30s warning
      if (configRef.current.sound_enabled && remainMs <= 30000 && remainMs > 0 && !warnPlayedRef.current) {
        playSound('warn');
        warnPlayedRef.current = true;
      }
      // Sound: alarm at 0
      if (configRef.current.sound_enabled && remainMs <= 0 && !alarmPlayedRef.current) {
        playSound('alarm');
        alarmPlayedRef.current = true;
      }
      // Auto-timeout
      if (elapsedMs > configRef.current.auto_timeout_min * 60 * 1000) {
        apiFetch(`/api/pack-timer/station/${stationId}/stop`, { method: 'POST' }).then(data => {
          if (data && data.result) {
            setLastResult(data.result);
            showCompleted(data.result);
          }
        });
      }
    }, 100);
    return () => clearInterval(iv);
  }, [displayState, session, stationId, apiFetch]);

  // ─── Show completed state for 3s ───
  const showCompleted = useCallback((result) => {
    setPreviousResult(result);
    setDisplayState('completed');
    if (configRef.current.sound_enabled) {
      playSound(result.effizienz_pct >= 100 ? 'success' : 'error');
    }
    if (completedTimerRef.current) clearTimeout(completedTimerRef.current);
    completedTimerRef.current = setTimeout(() => {
      setDisplayState('idle');
      setSession(null);
    }, 3000);
  }, []);

  // ─── Barcode Scanner ───
  const scanBufferRef = useRef('');
  const scanTimerRef = useRef(null);

  const handleScan = useCallback(async (barcode) => {
    if (!stationJwtRef.current || !stationId) return;

    // Normalize O→0
    const normalized = barcode.replace(/O/g, '0');

    // Try MA identify first
    const identRes = await apiFetch(`/api/pack-timer/station/${stationId}/identify`, {
      method: 'POST', body: JSON.stringify({ barcode: normalized }),
    });
    if (identRes && identRes.action === 'identified') {
      setMaName(identRes.mitarbeiter.name);
      setIdentifiedMa(identRes.mitarbeiter);
      setFlash({ type: 'success', text: `${t('packTimer.hallo', 'Hallo')} ${identRes.mitarbeiter.name}!` });
      setTimeout(() => setFlash(null), 3000);
      if (configRef.current.sound_enabled) playSound('start');
      return;
    }

    // Otherwise: scan order
    if (completedTimerRef.current) {
      clearTimeout(completedTimerRef.current);
      completedTimerRef.current = null;
    }

    const data = await apiFetch('/api/pack-timer/scan', {
      method: 'POST', body: JSON.stringify({ barcode: normalized, station_id: stationId }),
    });
    if (!data || data.error) {
      setFlash({ type: 'error', text: data?.error || 'Fehler' });
      setTimeout(() => setFlash(null), 3000);
      if (configRef.current.sound_enabled) playSound('error');
      return;
    }

    // Handle previous result
    if (data.previous_result) {
      setLastResult(data.previous_result);
    }

    // Start new session
    setSession(data.current);
    setConfig(data.config || configRef.current);
    setDisplayState('running');
    warnPlayedRef.current = false;
    alarmPlayedRef.current = false;

    if (configRef.current.sound_enabled) playSound('start');
  }, [stationId, apiFetch, t]);

  const handleScanRef = useRef(handleScan);
  useEffect(() => { handleScanRef.current = handleScan; }, [handleScan]);

  // ─── Simulator Buttons ───
  const simulateScan = useCallback(async () => {
    if (!stationJwtRef.current || !stationId) return;
    const barcode = 'SIM-' + Date.now();
    if (completedTimerRef.current) {
      clearTimeout(completedTimerRef.current);
      completedTimerRef.current = null;
    }
    const data = await apiFetch('/api/pack-timer/scan', {
      method: 'POST', body: JSON.stringify({ barcode, station_id: stationId }),
    });
    if (!data || data.error) {
      setFlash({ type: 'error', text: data?.error || 'Fehler' });
      setTimeout(() => setFlash(null), 3000);
      if (configRef.current.sound_enabled) playSound('error');
      return;
    }
    if (data.previous_result) setLastResult(data.previous_result);
    setSession(data.current);
    setConfig(c => ({ ...c, ...(data.config || {}) }));
    setDisplayState('running');
    warnPlayedRef.current = false;
    alarmPlayedRef.current = false;
    if (configRef.current.sound_enabled) playSound('start');
  }, [stationId, apiFetch]);

  const simulateStop = useCallback(async () => {
    if (!stationJwtRef.current || !stationId) return;
    const data = await apiFetch(`/api/pack-timer/station/${stationId}/stop`, { method: 'POST' });
    if (data && data.result) {
      setLastResult(data.result);
      showCompleted(data.result);
    }
  }, [stationId, apiFetch, showCompleted]);

  useEffect(() => {
    if (authState !== 'authenticated') return;
    const MAX_KEY_GAP = 150;
    const MIN_LEN = 4;

    const processBuffer = () => {
      const barcode = scanBufferRef.current.trim();
      scanBufferRef.current = '';
      if (barcode.length >= MIN_LEN) {
        handleScanRef.current(barcode);
      }
    };

    const handler = (e) => {
      if (e.key === 'Enter') {
        if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
        if (scanBufferRef.current.trim().length >= MIN_LEN) {
          e.preventDefault();
          processBuffer();
        }
        return;
      }
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
        scanBufferRef.current += e.key;
        scanTimerRef.current = setTimeout(processBuffer, MAX_KEY_GAP);
      }
    };

    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
      if (scanTimerRef.current) clearTimeout(scanTimerRef.current);
    };
  }, [authState]);

  // ─── PWA manifest for packplatz subdomain ───
  useEffect(() => {
    if (window.location.hostname.includes('packplatz')) {
      const existing = document.querySelector('link[rel="manifest"]');
      if (!existing) {
        const link = document.createElement('link');
        link.rel = 'manifest';
        link.href = '/packstation-manifest.json';
        document.head.appendChild(link);
      }
    }
  }, []);

  // ─── Disable zoom/context menu ───
  useEffect(() => {
    const prevent = e => e.preventDefault();
    document.addEventListener('contextmenu', prevent);
    const meta = document.querySelector('meta[name="viewport"]');
    const orig = meta?.content;
    if (meta) meta.content = 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no';
    return () => {
      document.removeEventListener('contextmenu', prevent);
      if (meta && orig) meta.content = orig;
    };
  }, []);

  // ─── Fullscreen ───
  const toggleFullscreen = useCallback(() => {
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {});
    else document.documentElement.requestFullscreen().catch(() => {});
  }, []);

  // ─── Helpers ───
  function formatTime(ms) {
    const neg = ms < 0;
    const abs = Math.abs(ms);
    const totalSec = Math.floor(abs / 1000);
    const min = Math.floor(totalSec / 60);
    const sec = totalSec % 60;
    const tenths = Math.floor((abs % 1000) / 100);
    return `${neg ? '+' : ''}${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}.${tenths}`;
  }

  function getTimerColor() {
    if (!session) return '#338edc';
    const sollMs = session.soll_minuten * 60 * 1000;
    const pct = (remaining / sollMs) * 100;
    if (remaining <= 0) return '#EF4444'; // Red — overtime
    if (pct > config.threshold_green) return '#10B981'; // Green
    if (pct > config.threshold_yellow) return '#F59E0B'; // Yellow
    return '#EF4444'; // Red
  }

  function getRingProgress() {
    if (!session) return 0;
    const sollMs = session.soll_minuten * 60 * 1000;
    if (remaining <= 0) return 100;
    return Math.max(0, Math.min(100, ((sollMs - remaining) / sollMs) * 100));
  }

  const clockStr = clock.toLocaleTimeString('de-AT', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  const dateStr = clock.toLocaleDateString('de-AT', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });

  // ─── Loading ───
  if (authState === 'checking') {
    return (
      <div style={S.root}>
        <div style={S.centerCol}>
          <div style={{ fontSize: 'clamp(1.2rem, 2vw, 1.5rem)', color: '#9CA3AF' }}>...</div>
        </div>
      </div>
    );
  }

  // ─── PIN Login Screen ───
  if (authState === 'pin') {
    return (
      <div style={S.root}>
        <div style={S.topBar}>
          <div style={{ fontSize: 'clamp(0.9rem, 1.5vw, 1.1rem)', color: '#9CA3AF' }}>{dateStr}</div>
          <div style={S.clock}>{clockStr}</div>
        </div>
        <div style={S.centerCol}>
          <div style={{ fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', fontWeight: 700, color: '#1A1A2E', marginBottom: 8 }}>
            {t('packTimer.pinTitle', 'Packstation Login')}
          </div>
          <div style={{ fontSize: 'clamp(0.9rem, 1.5vw, 1.2rem)', color: '#6B7280', marginBottom: 32 }}>
            {t('packTimer.pinSubtitle', 'Station-PIN eingeben')}
          </div>

          {/* PIN dots */}
          <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
            {[0, 1, 2, 3].map(i => (
              <div key={i} style={{
                width: 'clamp(18px, 3vw, 24px)', height: 'clamp(18px, 3vw, 24px)',
                borderRadius: '50%', border: '2px solid #E5E7EB',
                background: i < pinInput.length ? '#338edc' : 'transparent',
                transition: 'background 0.15s',
              }} />
            ))}
          </div>

          {/* Error */}
          {pinError && (
            <div style={{ color: '#DC2626', fontSize: 'clamp(0.85rem, 1.2vw, 1rem)', marginBottom: 16, fontWeight: 600 }}>
              {pinError}
            </div>
          )}

          {/* Numpad */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 'clamp(8px, 1.5vw, 12px)', maxWidth: 'clamp(220px, 30vw, 320px)' }}>
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, null, 0, 'del'].map((key, i) => {
              if (key === null) return <div key={i} />;
              return (
                <button
                  key={i}
                  onClick={() => {
                    if (key === 'del') { setPinInput(p => p.slice(0, -1)); setPinError(''); }
                    else if (pinInput.length < 4) setPinInput(p => p + key);
                  }}
                  style={{
                    width: 'clamp(60px, 8vw, 80px)', height: 'clamp(60px, 8vw, 80px)',
                    borderRadius: 16, border: '1px solid #E5E7EB', background: '#F8F9FC',
                    fontSize: key === 'del' ? 'clamp(0.9rem, 1.5vw, 1.2rem)' : 'clamp(1.3rem, 2.5vw, 2rem)',
                    fontWeight: 600, color: '#1A1A2E', cursor: 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    transition: 'background 0.1s',
                    WebkitTapHighlightColor: 'transparent',
                  }}
                  onMouseDown={(e) => { e.currentTarget.style.background = '#E5E7EB'; }}
                  onMouseUp={(e) => { e.currentTarget.style.background = '#F8F9FC'; }}
                  onTouchStart={(e) => { e.currentTarget.style.background = '#E5E7EB'; }}
                  onTouchEnd={(e) => { e.currentTarget.style.background = '#F8F9FC'; }}
                >
                  {key === 'del' ? '⌫' : key}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════
  //  COMPLETED STATE (3s overlay)
  // ═══════════════════════════════
  if (displayState === 'completed' && previousResult) {
    const eff = previousResult.effizienz_pct;
    const isGood = eff >= 100;
    const bgColor = isGood ? '#ECFDF5' : '#FEF2F2';
    const fgColor = isGood ? '#059669' : '#DC2626';
    return (
      <div style={{ ...S.root, background: bgColor, transition: 'background 0.3s' }}>
        <div style={S.topBar}>
          <div style={S.clock}>{clockStr}</div>
        </div>
        <div style={S.centerCol}>
          <div style={{ fontSize: 'clamp(3rem, 8vw, 6rem)', fontWeight: 800, color: fgColor }}>
            {Math.round(eff)}%
          </div>
          <div style={{ fontSize: 'clamp(1rem, 2.5vw, 1.8rem)', color: '#4B5563', marginTop: 8 }}>
            {t('packTimer.effizienz', 'Effizienz')}
          </div>
          <div style={{ display: 'flex', gap: 'clamp(24px, 4vw, 60px)', marginTop: 'clamp(16px, 3vh, 32px)' }}>
            <div style={S.statBox}>
              <div style={{ ...S.statLabel }}>{t('packTimer.soll', 'SOLL')}</div>
              <div style={{ ...S.statVal, color: '#1A1A2E' }}>{previousResult.soll_minuten.toFixed(1)} min</div>
            </div>
            <div style={{ fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', color: '#9CA3AF', alignSelf: 'center' }}>&rarr;</div>
            <div style={S.statBox}>
              <div style={S.statLabel}>{t('packTimer.ist', 'IST')}</div>
              <div style={{ ...S.statVal, color: fgColor }}>{previousResult.ist_minuten.toFixed(1)} min</div>
            </div>
          </div>
          <div style={{ fontSize: 'clamp(0.9rem, 1.5vw, 1.2rem)', color: '#6B7280', marginTop: 'clamp(8px, 2vh, 16px)' }}>
            {previousResult.kunde_name} &bull; {previousResult.artikel_count} {t('packTimer.artikel', 'Artikel')}
          </div>
        </div>
      </div>
    );
  }

  // ═══════════════════════════════
  //  RUNNING STATE (Timer)
  // ═══════════════════════════════
  if (displayState === 'running' && session) {
    const timerColor = getTimerColor();
    const progress = getRingProgress();
    const circumference = 2 * Math.PI * 140;
    const dashOffset = circumference - (progress / 100) * circumference;

    return (
      <div style={{ ...S.root, background: remaining <= 0 ? '#FEF2F2' : '#fff', transition: 'background 0.5s' }}>
        <div style={S.topBar}>
          <div style={S.clock}>{clockStr}</div>
          {maName && <div style={{ fontSize: 'clamp(0.9rem, 1.5vw, 1.2rem)', color: '#4B5563' }}>{maName}</div>}
          {stationName && <div style={{ fontSize: 'clamp(0.8rem, 1.2vw, 1rem)', color: '#9CA3AF' }}>{stationName}</div>}
          <button onClick={logout} style={S.smallBtn}>{t('packTimer.abmelden', 'Abmelden')}</button>
        </div>

        <div style={S.centerCol}>
          {/* Timer Ring */}
          <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width="clamp(240px, 35vw, 380px)" height="clamp(240px, 35vw, 380px)" viewBox="0 0 320 320">
              {/* Background circle */}
              <circle cx="160" cy="160" r="140" fill="none" stroke="#E5E7EB" strokeWidth="12" />
              {/* Progress arc */}
              <circle cx="160" cy="160" r="140" fill="none" stroke={timerColor} strokeWidth="12"
                strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={dashOffset}
                transform="rotate(-90 160 160)" style={{ transition: 'stroke 0.3s, stroke-dashoffset 0.1s' }} />
            </svg>
            <div style={{ position: 'absolute', textAlign: 'center' }}>
              <div style={{
                fontSize: 'clamp(2.5rem, 7vw, 5rem)', fontWeight: 800, color: timerColor,
                fontVariantNumeric: 'tabular-nums', lineHeight: 1, transition: 'color 0.3s',
              }}>
                {formatTime(remaining)}
              </div>
              {remaining <= 0 && (
                <div style={{ fontSize: 'clamp(0.8rem, 1.2vw, 1rem)', color: '#EF4444', fontWeight: 600, marginTop: 4 }}>
                  {t('packTimer.ueberschreitung', 'UEBERSCHREITUNG')}
                </div>
              )}
            </div>
          </div>

          {/* Info line */}
          <div style={{ display: 'flex', gap: 'clamp(12px, 2vw, 32px)', marginTop: 'clamp(16px, 3vh, 32px)', flexWrap: 'wrap', justifyContent: 'center' }}>
            <InfoChip label={t('packTimer.kunde', 'Kunde')} value={session.kunde_name} />
            <InfoChip label={t('packTimer.artikel', 'Artikel')} value={session.artikel_count} />
            <InfoChip label="SKUs" value={session.sku_count} />
            <InfoChip label={t('packTimer.soll', 'SOLL')} value={`${session.soll_minuten.toFixed(1)} min`} />
            {session.is_sop && (
              <span style={{
                background: '#EBF4FD', color: '#338edc', padding: '6px 16px', borderRadius: 8,
                fontWeight: 700, fontSize: 'clamp(0.8rem, 1.2vw, 1rem)',
              }}>SOP</span>
            )}
          </div>

          {/* Simulator buttons */}
          {config.mode === 'simulator' && (
            <div style={{ display: 'flex', gap: 'clamp(12px, 2vw, 24px)', marginTop: 'clamp(12px, 2vh, 24px)' }}>
              <button
                onClick={simulateScan}
                style={{
                  padding: 'clamp(10px, 1.5vh, 16px) clamp(20px, 3vw, 36px)',
                  fontSize: 'clamp(0.9rem, 1.5vw, 1.2rem)', fontWeight: 700,
                  background: '#10B981', color: '#fff', border: 'none', borderRadius: 12,
                  cursor: 'pointer', minHeight: 48,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                {t('packTimer.simNext', 'Fertig (nächster)')}
              </button>
              <button
                onClick={simulateStop}
                style={{
                  padding: 'clamp(10px, 1.5vh, 16px) clamp(20px, 3vw, 36px)',
                  fontSize: 'clamp(0.9rem, 1.5vw, 1.2rem)', fontWeight: 700,
                  background: '#EF4444', color: '#fff', border: 'none', borderRadius: 12,
                  cursor: 'pointer', minHeight: 48,
                  WebkitTapHighlightColor: 'transparent',
                }}
              >
                {t('packTimer.simStop', 'Stopp')}
              </button>
            </div>
          )}
        </div>

        {/* Last result strip */}
        {lastResult && (
          <div style={S.bottomStrip}>
            <span style={{ color: '#9CA3AF' }}>{t('packTimer.letzter', 'Letzter')}:</span>{' '}
            <span style={{ color: '#1A1A2E', fontWeight: 600 }}>{lastResult.kunde_name}</span>{' '}
            <span style={{ color: lastResult.effizienz_pct >= 100 ? '#059669' : '#DC2626', fontWeight: 700 }}>
              {Math.round(lastResult.effizienz_pct)}%
            </span>{' '}
            <span style={{ color: '#6B7280' }}>
              ({lastResult.ist_minuten.toFixed(1)} / {lastResult.soll_minuten.toFixed(1)} min)
            </span>
          </div>
        )}
      </div>
    );
  }

  // ═══════════════════════════════
  //  IDLE STATE (Waiting for scan)
  // ═══════════════════════════════
  return (
    <div style={S.root}>
      {/* Flash message */}
      {flash && (
        <div style={{
          position: 'fixed', top: 24, left: '50%', transform: 'translateX(-50%)', zIndex: 100,
          padding: '12px 32px', borderRadius: 12, fontWeight: 700,
          fontSize: 'clamp(1rem, 2vw, 1.4rem)',
          background: flash.type === 'success' ? '#ECFDF5' : '#FEF2F2',
          color: flash.type === 'success' ? '#059669' : '#DC2626',
          border: `2px solid ${flash.type === 'success' ? '#059669' : '#DC2626'}`,
        }}>
          {flash.text}
        </div>
      )}

      <div style={S.topBar}>
        <div style={{ fontSize: 'clamp(0.9rem, 1.5vw, 1.1rem)', color: '#9CA3AF' }}>{dateStr}</div>
        <div style={S.clock}>{clockStr}</div>
        <button onClick={logout} style={S.smallBtn}>{t('packTimer.abmelden', 'Abmelden')}</button>
      </div>

      <div style={S.centerCol}>
        {/* Scan icon */}
        <svg width="clamp(60px, 10vw, 100px)" height="clamp(60px, 10vw, 100px)" viewBox="0 0 24 24" fill="none" stroke="#338edc" strokeWidth="1.5" style={{ marginBottom: 'clamp(8px, 2vh, 24px)', opacity: 0.6 }}>
          <path d="M3 7V5a2 2 0 012-2h2M17 3h2a2 2 0 012 2v2M21 17v2a2 2 0 01-2 2h-2M7 21H5a2 2 0 01-2-2v-2" strokeLinecap="round" strokeLinejoin="round" />
          <line x1="7" y1="12" x2="17" y2="12" strokeLinecap="round" />
          <line x1="7" y1="8" x2="17" y2="8" strokeLinecap="round" />
          <line x1="7" y1="16" x2="17" y2="16" strokeLinecap="round" />
        </svg>

        <div style={{
          fontSize: 'clamp(1.5rem, 4vw, 3rem)', fontWeight: 700, color: '#1A1A2E', textAlign: 'center',
        }}>
          {identifiedMa
            ? `${t('packTimer.hallo', 'Hallo')} ${identifiedMa.name}!`
            : t('packTimer.scanPrompt', 'Kiste scannen zum Starten')}
        </div>

        {identifiedMa && (
          <div style={{ fontSize: 'clamp(1rem, 2vw, 1.5rem)', color: '#6B7280', marginTop: 8 }}>
            {t('packTimer.scanHint', 'Scanne eine Kiste zum Starten')}
          </div>
        )}

        {!identifiedMa && maName && (
          <div style={{ fontSize: 'clamp(1rem, 2vw, 1.5rem)', color: '#4B5563', marginTop: 12 }}>
            {maName}
          </div>
        )}

        {/* Simulator button */}
        {config.mode === 'simulator' && (
          <button
            onClick={simulateScan}
            style={{
              marginTop: 'clamp(16px, 3vh, 32px)', padding: 'clamp(12px, 2vh, 18px) clamp(24px, 4vw, 48px)',
              fontSize: 'clamp(1rem, 2vw, 1.4rem)', fontWeight: 700,
              background: '#338edc', color: '#fff', border: 'none', borderRadius: 16,
              cursor: 'pointer', minHeight: 56,
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            {t('packTimer.simScan', 'Scan simulieren')}
          </button>
        )}
      </div>

      {/* Last result card */}
      {lastResult && (
        <div style={{
          position: 'absolute', bottom: 'clamp(60px, 8vh, 100px)', left: '50%', transform: 'translateX(-50%)',
          background: '#F8F9FC', border: '1px solid #E5E7EB', borderRadius: 12, padding: 'clamp(12px, 2vh, 20px) clamp(20px, 3vw, 40px)',
          display: 'flex', gap: 'clamp(16px, 3vw, 32px)', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 'clamp(0.75rem, 1vw, 0.85rem)', color: '#9CA3AF', textTransform: 'uppercase', fontWeight: 600 }}>
              {t('packTimer.letzterAuftrag', 'Letzter Auftrag')}
            </div>
            <div style={{ fontSize: 'clamp(1rem, 1.5vw, 1.2rem)', color: '#1A1A2E', fontWeight: 600 }}>{lastResult.kunde_name}</div>
          </div>
          <div style={{ width: 1, height: 36, background: '#E5E7EB' }} />
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 'clamp(1.2rem, 2vw, 1.8rem)', fontWeight: 800, color: lastResult.effizienz_pct >= 100 ? '#059669' : '#DC2626' }}>
              {Math.round(lastResult.effizienz_pct)}%
            </div>
            <div style={{ fontSize: 'clamp(0.7rem, 1vw, 0.85rem)', color: '#6B7280' }}>
              {lastResult.ist_minuten.toFixed(1)} / {lastResult.soll_minuten.toFixed(1)} min
            </div>
          </div>
        </div>
      )}

      {/* Bottom buttons */}
      <div style={{ position: 'absolute', bottom: 16, right: 16, display: 'flex', gap: 8 }}>
        <button onClick={toggleFullscreen} style={S.smallBtn}>
          {t('packTimer.vollbild', 'Vollbild')}
        </button>
      </div>

      {/* Station info */}
      <div style={{ position: 'absolute', bottom: 16, left: 16, fontSize: 'clamp(0.7rem, 1vw, 0.85rem)', color: '#9CA3AF' }}>
        {stationInfo ? `${stationInfo.name} (#${stationInfo.nr})` : `Station ${stationId}`}
      </div>
    </div>
  );
}


// ─── Info Chip ───
function InfoChip({ label, value }) {
  return (
    <div style={{
      background: '#F8F9FC', border: '1px solid #E5E7EB', borderRadius: 10,
      padding: '8px 16px', textAlign: 'center',
    }}>
      <div style={{ fontSize: 'clamp(0.65rem, 0.9vw, 0.8rem)', color: '#9CA3AF', textTransform: 'uppercase', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 'clamp(0.9rem, 1.4vw, 1.2rem)', color: '#1A1A2E', fontWeight: 700 }}>{value}</div>
    </div>
  );
}


// ─── Styles ───
const S = {
  root: {
    minHeight: '100vh', maxHeight: '100vh', overflow: 'hidden',
    background: '#fff', fontFamily: "'DM Sans', sans-serif",
    display: 'flex', flexDirection: 'column', position: 'relative',
    WebkitUserSelect: 'none', userSelect: 'none', touchAction: 'manipulation',
  },
  topBar: {
    display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 16,
    padding: 'clamp(8px, 1.5vh, 16px) clamp(16px, 2vw, 32px)',
  },
  clock: {
    fontSize: 'clamp(1rem, 2vw, 1.5rem)', fontWeight: 600, color: '#1A1A2E',
    fontVariantNumeric: 'tabular-nums',
  },
  centerCol: {
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', padding: '0 clamp(16px, 3vw, 48px)',
  },
  statBox: { textAlign: 'center' },
  statLabel: { fontSize: 'clamp(0.75rem, 1.2vw, 1rem)', color: '#9CA3AF', textTransform: 'uppercase', fontWeight: 600 },
  statVal: { fontSize: 'clamp(1.5rem, 3vw, 2.5rem)', fontWeight: 800 },
  bottomStrip: {
    padding: 'clamp(8px, 1.5vh, 14px) clamp(16px, 2vw, 32px)',
    borderTop: '1px solid #E5E7EB', textAlign: 'center',
    fontSize: 'clamp(0.85rem, 1.2vw, 1.1rem)',
  },
  smallBtn: {
    background: '#F8F9FC', border: '1px solid #E5E7EB', borderRadius: 8,
    padding: '6px 14px', fontSize: '0.8rem', color: '#6B7280', cursor: 'pointer',
  },
};
